import { createEventNotifier } from "./event-notifier";

export const reservedArgValues = ["one", "some", "every", "none"] as const;
export type ReservedArgValue = (typeof reservedArgValues)[number];

export type ConditionUpdate = [string, boolean];

type ConditionSubscription = {
  tokens: string[];

  callback: (values: (boolean | null)[]) => void;
};

export type ConditionArgOptions = {
  id: string;
  name: string;
};

export type ConditionKeyOptions = {
  id: string;
  name: string;

  args?: ConditionArgOptions[];
};

export type ConditionGroupOptions = {
  id: string;
  name: string;

  args?: ConditionArgOptions[];

  keys: ConditionKeyOptions[];
};

export type ConditionMapOptions = {
  oneGroupOptions?: ConditionGroupOptions;
};

export type ConditionMapUpdateCallback = () => void;

export class ConditionMap {
  private readonly _updateEventNotifier =
    createEventNotifier<ConditionMapUpdateCallback>();

  private readonly _groupById = new Map<string, ConditionGroupOptions>();

  private readonly _leafsByMinimalToken: Map<string, Set<string>> = new Map();
  private readonly _valueByLeaf: Map<string, boolean> = new Map();

  private readonly _subscribersByMinimalToken: Map<
    string,
    Set<ConditionSubscription>
  > = new Map();

  private readonly _oneGroupId: string | null = null;

  constructor(options: ConditionMapOptions = {}) {
    if (options.oneGroupOptions) {
      this._oneGroupId = options.oneGroupOptions.id;

      this.registerGroup(options.oneGroupOptions);
    }
  }

  // #region Tokenization

  static isReservedArg(arg: string): arg is ReservedArgValue {
    return reservedArgValues.includes(arg as ReservedArgValue);
  }

  static tokenize(...parts: string[]) {
    return parts.join(":");
  }

  private _parseTokenParts(token: string) {
    const tokenParts = token.split(":");

    const groupId = this._oneGroupId ?? tokenParts.at(0);
    const keyId = tokenParts.at(-1);
    const argValues = tokenParts.slice(this._oneGroupId ? 0 : 1, -1);

    if (!groupId) {
      throw new SyntaxError(`Condition group is missing`);
    }

    if (!keyId) {
      throw new SyntaxError(`Condition key is missing`);
    }

    const minimalToken = ConditionMap.tokenize(groupId, keyId);

    return { groupId, keyId, argValues, minimalToken };
  }

  private _parseToken(token: string) {
    const { groupId, keyId, argValues, minimalToken } =
      this._parseTokenParts(token);

    const group = this._groupById.get(groupId);

    if (!group) {
      throw new SyntaxError(`Condition group ${groupId} is not registered`);
    }

    const key = group.keys.find((key) => key.id === keyId);

    if (!key) {
      throw new SyntaxError(`Condition key ${keyId} is not registered`);
    }

    const args = (group.args ?? []).concat(key.args ?? []);

    if (args.length !== argValues.length) {
      throw new SyntaxError(
        `Condition key ${keyId} has ${args.length} args, but ${argValues.length} were provided`,
      );
    }

    const isAggregate = argValues.some((value) =>
      ConditionMap.isReservedArg(value),
    );

    for (const v of argValues) {
      if (!v || v.includes(":") || v.includes("*")) {
        throw new SyntaxError(`Invalid arg value "${v}"`);
      }
    }

    return {
      group,
      key,

      args,
      argValues,
      isLeaf: !isAggregate,
      isAggregate: isAggregate,

      minimalToken,
    };
  }

  // #endregion

  registerGroup(options: ConditionGroupOptions) {
    const { id } = options;

    if (this._groupById.has(id)) {
      throw new Error(`Condition group ${id} is already registered`);
    }

    options.keys.forEach((key) => {
      this._leafsByMinimalToken.set(
        ConditionMap.tokenize(id, key.id),
        new Set(),
      );
    });

    this._groupById.set(id, options);
  }

  // #region Computation

  private _computeAggregate(
    parsed: ReturnType<ConditionMap["_parseToken"]>,
  ): boolean | null {
    const { group, key, argValues, minimalToken } = parsed;

    const candidateLeaves = Array.from(
      this._leafsByMinimalToken.get(minimalToken)!,
    );

    const computeAggregateRecursive = (
      groupId: string,
      keyId: string,
      argValues: string[],
      pos: number,
      candidateLeaves: string[],
    ): boolean | null => {
      if (pos >= argValues.length) {
        if (candidateLeaves.length === 0) {
          // The token fixes a concrete value that does not exist among known leaves.
          return null;
        }

        if (candidateLeaves.length === 1) {
          return this._valueByLeaf.get(candidateLeaves[0]) ?? null;
        }

        throw new Error(
          `Invariant violated: multiple leaves at end of arguments (got ${candidateLeaves.length})`,
        );
      }

      const currentArgValue = argValues[pos];

      // Concrete value: filter candidates matching the current position and descend.
      if (!ConditionMap.isReservedArg(currentArgValue)) {
        const nextCandidates: string[] = [];

        for (const leaf of candidateLeaves) {
          const { argValues } = this._parseTokenParts(leaf);

          if (argValues[pos] === currentArgValue) {
            nextCandidates.push(leaf);
          }
        }

        return computeAggregateRecursive(
          groupId,
          keyId,
          argValues,
          pos + 1,
          nextCandidates,
        );
      }

      // Reserved value: partition by the concrete value observed at this position,
      const leafsByArgValue = new Map<string, string[]>();
      for (const leaf of candidateLeaves) {
        const { argValues } = this._parseTokenParts(leaf);

        const candidateArgValue = argValues[pos];
        let arr = leafsByArgValue.get(candidateArgValue);
        if (!arr) {
          arr = [];
          leafsByArgValue.set(candidateArgValue, arr);
        }
        arr.push(leaf);
      }

      let trueCount = 0;

      // Evaluate each concrete branch (each distinct value at this position).
      for (const subLeaves of leafsByArgValue.values()) {
        const value = computeAggregateRecursive(
          groupId,
          keyId,
          argValues,
          pos + 1,
          subLeaves,
        );

        if (value === null) {
          return null;
        }

        if (value) {
          ++trueCount;
        }
      }

      // Apply the operator.
      switch (currentArgValue) {
        case "one":
          return trueCount === 1;
        case "some":
          return trueCount > 0;
        case "every":
          return trueCount === leafsByArgValue.size;
        case "none":
          return trueCount === 0;
      }
    };

    return computeAggregateRecursive(
      group.id,
      key.id,
      argValues,
      0,
      candidateLeaves,
    );
  }

  // #endregion

  get(token: string): boolean | null {
    const parsedToken = this._parseToken(token);

    const { isLeaf } = parsedToken;

    if (isLeaf) {
      return this._valueByLeaf.get(token) ?? null;
    }

    return this._computeAggregate(parsedToken);
  }

  is(token: string): boolean {
    return this.get(token) ?? false;
  }

  // #region Updates

  private _setLeaf(token: string, value: boolean): boolean {
    const { isAggregate, minimalToken } = this._parseToken(token);

    if (isAggregate) {
      throw new SyntaxError(`Reserved arguments are not allowed in set`);
    }

    this._leafsByMinimalToken.get(minimalToken)!.add(token);

    const previousValue = this._valueByLeaf.get(token);

    if (previousValue === value) {
      return false;
    }

    this._valueByLeaf.set(token, value);

    return true;
  }

  set(updates: ConditionUpdate[]) {
    const updatedMinimalTokens = new Set<string>();

    for (const [token, value] of updates) {
      const { minimalToken } = this._parseTokenParts(token);

      const isUpdated = this._setLeaf(token, value);

      if (isUpdated) {
        updatedMinimalTokens.add(minimalToken);
      }
    }

    const toNotify = new Set<ConditionSubscription>();

    for (const minimalToken of updatedMinimalTokens) {
      const subscriptions = this._subscribersByMinimalToken.get(minimalToken);

      if (!subscriptions) {
        continue;
      }

      for (const subscription of subscriptions) {
        toNotify.add(subscription);
      }
    }

    for (const subscription of toNotify) {
      this._notify(subscription);
    }

    if (toNotify.size > 0) {
      this._updateEventNotifier.notifyListeners();
    }
  }

  onUpdate(callback: ConditionMapUpdateCallback) {
    this._updateEventNotifier.addListener(callback);
  }

  // #endregion

  // #region Debug

  /**
   * DEBUG helper: compute all aggregate combinations reachable from known leaves.
   * WARNING: Can be huge (exponential in the number of argument positions).
   */
  get allCombinations(): Map<string, boolean | null> {
    const results = new Map<string, boolean | null>();

    for (const leaf of this._valueByLeaf.keys()) {
      const { groupId, keyId, argValues } = this._parseTokenParts(leaf);
      for (let mask = 0; mask < 1 << argValues.length; mask++) {
        for (let i = 0; i < argValues.length; i++) {
          if (mask & (1 << i)) {
            for (const r of reservedArgValues) {
              const candidate = [
                groupId,
                ...argValues.slice(0, i),
                r,
                ...argValues.slice(i + 1),
                keyId,
              ];
              const token = ConditionMap.tokenize(...candidate);
              if (!results.has(token)) {
                results.set(token, this.get(token));
              }
            }
          }
        }
      }

      results.set(leaf, this.get(leaf));
    }

    return results;
  }

  // #endregion

  // #region Subscriptions

  private _notify(subscription: ConditionSubscription) {
    const { tokens, callback } = subscription;

    callback(tokens.map((token) => this.get(token)));
  }

  subscribe(subscription: ConditionSubscription) {
    const { tokens } = subscription;

    for (const token of tokens) {
      const { minimalToken } = this._parseTokenParts(token);

      if (!this._subscribersByMinimalToken.has(minimalToken)) {
        this._subscribersByMinimalToken.set(minimalToken, new Set());
      }

      this._subscribersByMinimalToken.get(minimalToken)!.add(subscription);
    }
  }

  // #endregion
}
